import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, ImagePlus, Star, X } from 'lucide-react';
import { useContext, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';

import { ApiError } from '@/api/client';
import {
  MAX_REVIEW_PHOTOS,
  type ProductReviews as ProductReviewsPage,
  type ReviewDraft,
  type ReviewSortOrder,
  type StorefrontOwnReview,
  type StorefrontReview,
} from '@/features/storefront/api/storefront';
import { CustomerAuthContext } from '@/features/storefront/account/customerAuthContext';
import { REVIEWS_ANCHOR, StarRating } from '@/features/storefront/components/StarRating';
import { StorefrontContainer as Container } from '@/features/storefront/components/StorefrontContainer';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent, type ContentReader } from '@/features/storefront/content/useContent';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { placeholderReviews } from '@/features/storefront/sections/placeholderContent';
import {
  REVIEW_SORTS,
  type ProductReviewsSection,
} from '@/features/storefront/sections/schema';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/i18n/format';
import { ImageTooLargeError, prepareImageForUpload } from '@/lib/imageProcessor';

/**
 * What customers said about the product on this page, and the form to add to it.
 *
 * The reviews are the server's - written by signed-in customers, approved in
 * the dashboard - so, unlike `testimonials`, nothing here is the merchant's own
 * copy except the heading. Three callers, three answers:
 *
 * - **The shopper's product page** reads the product's published reviews
 *   through the data source. A shop whose plan has no reviews answers 404, and
 *   the section then draws nothing at all: a "reviews" band that can never
 *   hold a review is a promise the shop cannot keep.
 * - **A theme's sample shop** answers from its own sample reviews, through the
 *   same source, so the section is judged with something in it.
 * - **The customiser** has no product being shopped, so it draws stand-ins
 *   (`placeholderReviews`) through this same markup, and the form is inert.
 *
 * Anywhere else - a custom page, the home page - there is no product to review,
 * and the section renders nothing.
 */
export interface ProductReviewsProps {
  section: ProductReviewsSection;
  /** The product the page is showing, or null where there is none. */
  product: { slug: string; name: string } | null;
  /** True in the customiser: stand-in reviews, nothing sent anywhere. */
  preview: boolean;
  storeName: string;
  /** The section's heading, drawn by the renderer so it stays typeable in the customiser. */
  heading: ReactNode;
}

const SORT_KEYS: Record<ReviewSortOrder, string> = {
  newest: 'reviews.sort_newest',
  highest: 'reviews.sort_highest',
  lowest: 'reviews.sort_lowest',
  photos: 'reviews.sort_photos',
};

const FIELD_STYLE = {
  borderColor: 'var(--storefront-muted)',
  borderRadius: 'var(--storefront-radius)',
  backgroundColor: 'var(--storefront-background)',
  color: 'var(--storefront-text)',
};

const MUTED = { color: 'var(--storefront-muted)' };

export function ProductReviews(props: ProductReviewsProps) {
  if (props.preview) return <ReviewsBody {...props} data={placeholderReviews(props.section.page_size)} />;
  if (!props.product) return null;
  return <LiveReviews {...props} product={props.product} />;
}

/** The shopper's side: the reviews come from the data source, a page at a time. */
function LiveReviews(props: ProductReviewsProps & { product: { slug: string; name: string } }) {
  const { section, product } = props;
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const [sort, setSort] = useState<ReviewSortOrder>(section.default_sort);
  const [rating, setRating] = useState<number | null>(null);

  const reviews = useInfiniteQuery({
    queryKey: [...scope, 'product-reviews', product.slug, { sort, rating, size: section.page_size }],
    queryFn: ({ pageParam }) =>
      source.productReviews(product.slug, { page: pageParam, pageSize: section.page_size, sort, rating }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.page_size < last.total ? last.page + 1 : undefined),
    // A 404 is the plan saying "no reviews here", which is not going to change
    // by asking again.
    retry: false,
  });

  // Nothing while the first page is on its way, and nothing for good on a
  // plan without reviews - a heading that appears and then vanishes is worse
  // than a heading that arrives with its reviews.
  if (reviews.isPending) return null;
  if (reviews.isError) {
    if (reviews.error instanceof ApiError && reviews.error.status === 404) return null;
    return <ReviewsError heading={props.heading} />;
  }

  const [first] = reviews.data.pages;
  const merged: ProductReviewsPage = {
    ...first,
    items: reviews.data.pages.flatMap((page) => page.items),
  };

  return (
    <ReviewsBody
      {...props}
      data={merged}
      sort={sort}
      onSort={setSort}
      rating={rating}
      onRating={setRating}
      onMore={reviews.hasNextPage ? () => void reviews.fetchNextPage() : undefined}
      loadingMore={reviews.isFetchingNextPage}
    />
  );
}

function ReviewsError({ heading }: { heading: ReactNode }) {
  const copy = useShopContent();
  return (
    <Container>
      <div id={REVIEWS_ANCHOR} className="scroll-mt-24">
        {heading}
        <p className="text-sm" style={MUTED}>
          {copy('reviews.load_error')}
        </p>
      </div>
    </Container>
  );
}

interface BodyProps extends ProductReviewsProps {
  data: ProductReviewsPage;
  sort?: ReviewSortOrder;
  onSort?: (sort: ReviewSortOrder) => void;
  rating?: number | null;
  onRating?: (rating: number | null) => void;
  onMore?: () => void;
  loadingMore?: boolean;
}

/** The section's markup, whoever is supplying the reviews. */
function ReviewsBody({
  section,
  product,
  preview,
  storeName,
  heading,
  data,
  sort = section.default_sort,
  onSort,
  rating = null,
  onRating,
  onMore,
  loadingMore = false,
}: BodyProps) {
  const copy = useShopContent();
  const { summary, items } = data;
  const hasAny = summary.count > 0;

  return (
    <Container>
      {/* The anchor the stars beside the price link to. */}
      <div id={REVIEWS_ANCHOR} className="scroll-mt-24">
        {heading}

        <div
          className={cn(
            'grid gap-8',
            hasAny && section.show_breakdown && 'lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-12',
          )}
        >
          {hasAny && section.show_breakdown ? (
            <div>
              <Summary summary={summary} copy={copy} />
              <div className="mt-6">
                <WriteReview product={product} preview={preview} />
              </div>
            </div>
          ) : null}

          <div className="min-w-0">
            {hasAny ? (
              <>
                {!section.show_breakdown ? (
                  <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                    <CompactSummary summary={summary} copy={copy} />
                    <WriteReview product={product} preview={preview} />
                  </div>
                ) : null}
                <Controls
                  sort={sort}
                  onSort={onSort}
                  rating={rating}
                  onRating={onRating}
                  distribution={summary.distribution}
                />
                {items.length === 0 ? (
                  <p className="py-6 text-sm" style={MUTED}>
                    {copy('reviews.empty_filtered')}
                  </p>
                ) : (
                  <ul
                    className={cn(
                      'mt-6',
                      section.layout === 'grid' ? 'grid gap-4 sm:grid-cols-2' : 'grid gap-4',
                    )}
                  >
                    {items.map((review) => (
                      <li key={review.id}>
                        <ReviewCard review={review} showPhotos={section.show_photos} storeName={storeName} />
                      </li>
                    ))}
                  </ul>
                )}
                {onMore ? (
                  <div className="mt-6 text-center">
                    <button
                      type="button"
                      onClick={onMore}
                      disabled={loadingMore}
                      className="h-11 border px-6 text-sm font-semibold disabled:opacity-60"
                      style={{
                        borderColor: 'var(--storefront-text)',
                        borderRadius: 'var(--storefront-radius)',
                      }}
                    >
                      {copy('reviews.load_more')}
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <div
                className="flex flex-col items-start gap-4 p-6"
                style={{
                  backgroundColor: 'var(--storefront-surface)',
                  borderRadius: 'var(--storefront-radius)',
                }}
              >
                <p className="text-base font-semibold">{copy('reviews.empty')}</p>
                <WriteReview product={product} preview={preview} />
              </div>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
}

// --- The summary -------------------------------------------------------------
function Summary({ summary, copy }: { summary: ProductReviewsPage['summary']; copy: ContentReader }) {
  const average = summary.average ?? 0;
  return (
    <div>
      <p className="text-4xl font-semibold" style={STOREFRONT_HEADING_STYLE}>
        {average.toFixed(1)}
      </p>
      <StarRating
        value={average}
        size={18}
        className="mt-2"
        label={copy('reviews.summary_label', { rating: average.toFixed(1), count: summary.count })}
      />
      <p className="mt-1 text-sm" style={MUTED}>
        {summary.count === 1
          ? copy('reviews.based_on_single')
          : copy('reviews.based_on', { count: summary.count })}
      </p>
      <ul className="mt-5 space-y-2">
        {[5, 4, 3, 2, 1].map((stars) => {
          const count = summary.distribution[String(stars)] ?? 0;
          const share = summary.count ? (count / summary.count) * 100 : 0;
          return (
            <li key={stars} className="flex items-center gap-3 text-sm">
              <span className="w-16 shrink-0">{copy('reviews.stars', { rating: stars })}</span>
              <span
                className="h-2 flex-1 overflow-hidden"
                style={{ backgroundColor: 'var(--storefront-surface)', borderRadius: 999 }}
                aria-hidden
              >
                <span
                  className="block h-full"
                  style={{ width: `${share}%`, backgroundColor: 'var(--storefront-primary)', borderRadius: 999 }}
                />
              </span>
              <span className="w-8 shrink-0 text-end tabular-nums" style={MUTED}>
                {count}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The one-line version, for a section with the breakdown switched off. */
function CompactSummary({ summary, copy }: { summary: ProductReviewsPage['summary']; copy: ContentReader }) {
  const average = summary.average ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <StarRating
        value={average}
        size={18}
        label={copy('reviews.summary_label', { rating: average.toFixed(1), count: summary.count })}
      />
      <span className="font-semibold">{average.toFixed(1)}</span>
      <span style={MUTED}>
        {summary.count === 1
          ? copy('reviews.based_on_single')
          : copy('reviews.based_on', { count: summary.count })}
      </span>
    </div>
  );
}

// --- Sorting and filtering ---------------------------------------------------
function Controls({
  sort,
  onSort,
  rating,
  onRating,
  distribution,
}: {
  sort: ReviewSortOrder;
  onSort?: (sort: ReviewSortOrder) => void;
  rating: number | null;
  onRating?: (rating: number | null) => void;
  distribution: Record<string, number>;
}) {
  const copy = useShopContent();
  const chip = (active: boolean) => ({
    borderColor: active ? 'var(--storefront-primary)' : 'var(--storefront-surface)',
    backgroundColor: active ? 'var(--storefront-primary)' : 'transparent',
    color: active ? 'var(--storefront-background)' : 'var(--storefront-text)',
    borderRadius: 999,
  });

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div role="group" aria-label={copy('reviews.filter_label')} className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-pressed={rating === null}
          onClick={() => onRating?.(null)}
          className="h-8 border px-3 text-xs font-medium"
          style={chip(rating === null)}
        >
          {copy('reviews.filter_all')}
        </button>
        {[5, 4, 3, 2, 1]
          // A chip that can only lead to "no reviews" is left off - except the
          // one the shopper is standing on, so they can see what they chose.
          .filter((stars) => (distribution[String(stars)] ?? 0) > 0 || rating === stars)
          .map((stars) => (
            <button
              key={stars}
              type="button"
              aria-pressed={rating === stars}
              onClick={() => onRating?.(stars)}
              className="inline-flex h-8 items-center gap-1 border px-3 text-xs font-medium"
              style={chip(rating === stars)}
            >
              {copy('reviews.stars', { rating: stars })}
            </button>
          ))}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <span style={MUTED}>{copy('reviews.sort_label')}</span>
        <select
          value={sort}
          onChange={(event) => onSort?.(event.target.value as ReviewSortOrder)}
          className="h-9 border px-2 text-sm"
          style={FIELD_STYLE}
        >
          {REVIEW_SORTS.map((each) => (
            <option key={each} value={each}>
              {copy(SORT_KEYS[each])}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

// --- One review --------------------------------------------------------------
function ReviewCard({
  review,
  showPhotos,
  storeName,
  status,
}: {
  review: StorefrontReview;
  showPhotos: boolean;
  storeName: string;
  /** The shopper's own review carries its moderation state. */
  status?: ReactNode;
}) {
  const copy = useShopContent();
  const { locale } = useStorefrontLanguage();

  return (
    <article
      className="h-full p-5"
      style={{ backgroundColor: 'var(--storefront-surface)', borderRadius: 'var(--storefront-radius)' }}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <StarRating value={review.rating} label={copy('section.rating', { rating: review.rating })} />
        {status}
      </div>
      {review.title ? <h3 className="mt-3 text-base font-semibold">{review.title}</h3> : null}
      <p className={cn('whitespace-pre-line text-sm leading-relaxed', review.title ? 'mt-1' : 'mt-3')}>
        {review.body}
      </p>

      {showPhotos && review.photos.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {review.photos.map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer">
              <StorefrontImage
                url={url}
                sizes="80px"
                alt={copy('reviews.photo_alt', { author: review.author_name })}
                className="size-20 object-cover"
                style={{ borderRadius: 'var(--storefront-radius)' }}
              />
            </a>
          ))}
        </div>
      ) : null}

      <p className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" style={MUTED}>
        <span className="font-semibold" style={{ color: 'var(--storefront-text)' }}>
          {review.author_name}
        </span>
        {review.verified_purchase ? (
          <span className="inline-flex items-center gap-1" style={{ color: 'var(--storefront-primary)' }}>
            <BadgeCheck className="size-3.5" aria-hidden />
            {copy('reviews.verified')}
          </span>
        ) : null}
        <span>·</span>
        <time dateTime={review.created_at}>{formatDate(review.created_at, locale)}</time>
      </p>

      {review.merchant_reply ? (
        <div
          className="mt-4 border-s-2 ps-3 text-sm"
          style={{ borderColor: 'var(--storefront-primary)' }}
        >
          <p className="text-xs font-semibold">{copy('reviews.reply', { store: storeName })}</p>
          <p className="mt-1 whitespace-pre-line leading-relaxed" style={MUTED}>
            {review.merchant_reply}
          </p>
        </div>
      ) : null}
    </article>
  );
}

// --- Writing one -------------------------------------------------------------
/**
 * The way in to writing a review, whoever is looking.
 *
 * The customer is read from the context directly rather than through
 * `useCustomerAuth`, which throws outside a provider: the customiser draws
 * this section with no shopper session around it at all, and a theme's static
 * preview may not either. No provider reads as "nobody signed in".
 */
function WriteReview({ product, preview }: { product: { slug: string; name: string } | null; preview: boolean }) {
  const copy = useShopContent();
  const auth = useContext(CustomerAuthContext);
  const paths = useStorefrontPaths();
  const location = useLocation();

  if (preview || !product) {
    // Inert in the customiser: the merchant is looking at where the link
    // stands, and following it would take them out of the editor.
    return (
      <span className="inline-flex h-11 items-center px-6 text-sm font-semibold" style={STOREFRONT_BUTTON_STYLE}>
        {copy('product.write_review')}
      </span>
    );
  }

  if (!auth || auth.status === 'anonymous') {
    return (
      <Link
        to={paths.accountLogin()}
        state={{ from: `${location.pathname}${location.search}` }}
        className="inline-flex h-11 items-center px-6 text-sm font-semibold"
        style={STOREFRONT_BUTTON_STYLE}
      >
        {copy('reviews.login')}
      </Link>
    );
  }

  // Still recovering the session from the refresh cookie. Saying "log in" now
  // would be wrong for most of the shoppers who see it.
  if (auth.status === 'loading') return null;

  return <OwnReview slug={product.slug} />;
}

/** A signed-in shopper: their review if they have one, and the form either way. */
function OwnReview({ slug }: { slug: string }) {
  const copy = useShopContent();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const queryClient = useQueryClient();
  // Under `account`, so signing out - which invalidates that branch - drops it.
  const mineKey = [...scope, 'account', 'product-review', slug];
  const mine = useQuery({ queryKey: mineKey, queryFn: () => source.myProductReview(slug), retry: false });
  const [editing, setEditing] = useState(false);
  const [thanks, setThanks] = useState(false);

  if (mine.isPending) return null;
  // Their own review failing to load is no reason to hide everyone else's; the
  // form stays out of the way until it can tell a first review from an edit.
  if (mine.isError) return null;

  const own = mine.data;
  const done = (saved: StorefrontOwnReview) => {
    queryClient.setQueryData(mineKey, saved);
    // A shop that publishes straight away has just gained a review.
    void queryClient.invalidateQueries({ queryKey: [...scope, 'product-reviews', slug] });
    setEditing(false);
    setThanks(true);
  };

  if (own && !editing) {
    return (
      <div className="w-full space-y-3">
        <p className="text-sm font-semibold">{copy('reviews.yours')}</p>
        <OwnReviewCard review={own} />
        {thanks ? (
          <p role="status" className="text-sm font-semibold" style={{ color: 'var(--storefront-primary)' }}>
            {copy('reviews.thanks')}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setThanks(false);
            setEditing(true);
          }}
          className="h-10 border px-5 text-sm font-semibold"
          style={{ borderColor: 'var(--storefront-text)', borderRadius: 'var(--storefront-radius)' }}
        >
          {copy('reviews.edit')}
        </button>
      </div>
    );
  }

  return (
    <ReviewForm
      slug={slug}
      existing={own}
      onSaved={done}
      onCancel={own ? () => setEditing(false) : undefined}
      onConflict={() => void mine.refetch()}
    />
  );
}

function OwnReviewCard({ review }: { review: StorefrontOwnReview }) {
  const copy = useShopContent();
  const label =
    review.status === 'PENDING'
      ? copy('reviews.status_pending')
      : review.status === 'HIDDEN'
        ? copy('reviews.status_hidden')
        : copy('reviews.status_published');
  return (
    <ReviewCard
      review={review}
      showPhotos
      storeName=""
      status={
        <span
          className="px-2 py-0.5 text-xs font-medium"
          style={{
            borderRadius: 999,
            backgroundColor: 'var(--storefront-background)',
            color: review.status === 'PUBLISHED' ? 'var(--storefront-primary)' : 'var(--storefront-muted)',
          }}
        >
          {label}
        </span>
      }
    />
  );
}

function ReviewForm({
  slug,
  existing,
  onSaved,
  onCancel,
  onConflict,
}: {
  slug: string;
  existing: StorefrontOwnReview | null;
  onSaved: (saved: StorefrontOwnReview) => void;
  onCancel?: () => void;
  /** The server already has a review from them - show that one instead. */
  onConflict: () => void;
}) {
  const copy = useShopContent();
  const source = useStorefrontData();
  // Closed behind a button for a first review, so a product page does not
  // open on an empty form; an edit opens straight into it.
  const [open, setOpen] = useState(Boolean(existing));
  const [rating, setRating] = useState(existing?.rating ?? 0);
  const [title, setTitle] = useState(existing?.title ?? '');
  const [body, setBody] = useState(existing?.body ?? '');
  const [photos, setPhotos] = useState<string[]>(existing?.photos ?? []);
  const [problem, setProblem] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (draft: ReviewDraft) =>
      existing ? source.updateMyProductReview(slug, draft) : source.submitProductReview(slug, draft),
    onSuccess: onSaved,
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        setProblem(copy('reviews.exists'));
        onConflict();
        return;
      }
      setProblem(copy(reviewErrorKey(error)));
    },
  });

  // Uploaded as soon as it is picked, so the review itself posts only URLs.
  // Resized and turned into WebP on the way, like every other upload here: a
  // phone photo is several megabytes that a review thumbnail never needs.
  const upload = useMutation({
    mutationFn: async (file: File) => source.uploadReviewPhoto(slug, await prepareImageForUpload(file)),
    onSuccess: (url) => setPhotos((current) => [...current, url].slice(0, MAX_REVIEW_PHOTOS)),
    onError: (error) =>
      setProblem(
        copy(
          error instanceof ImageTooLargeError
            ? 'reviews.photo_too_large'
            : reviewErrorKey(error, 'reviews.photo_error'),
        ),
      ),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="h-11 px-6 text-sm font-semibold"
        style={STOREFRONT_BUTTON_STYLE}
      >
        {copy('product.write_review')}
      </button>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!rating) return setProblem(copy('reviews.rating_required'));
    if (!body.trim()) return setProblem(copy('reviews.body_required'));
    setProblem(null);
    save.mutate({ rating, title: title.trim() || null, body: body.trim(), photos });
  };

  return (
    <form onSubmit={submit} className="w-full space-y-4" noValidate>
      <StarPicker value={rating} onChange={setRating} />
      <label className="block text-sm">
        <span className="mb-1 block font-medium">{copy('reviews.title_label')}</span>
        <input
          value={title}
          maxLength={120}
          onChange={(event) => setTitle(event.target.value)}
          className="h-11 w-full border px-3 text-sm"
          style={FIELD_STYLE}
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">{copy('reviews.body_label')}</span>
        <textarea
          value={body}
          maxLength={3000}
          rows={5}
          required
          onChange={(event) => setBody(event.target.value)}
          className="w-full border px-3 py-2 text-sm"
          style={FIELD_STYLE}
        />
      </label>
      <PhotoPicker
        photos={photos}
        uploading={upload.isPending}
        onPick={(file) => {
          setProblem(null);
          upload.mutate(file);
        }}
        onRemove={(url) => setPhotos((current) => current.filter((each) => each !== url))}
      />
      {problem ? (
        <p role="alert" className="text-sm" style={{ color: 'var(--storefront-primary)' }}>
          {problem}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={save.isPending || upload.isPending}
          className="h-11 px-6 text-sm font-semibold disabled:opacity-60"
          style={STOREFRONT_BUTTON_STYLE}
        >
          {save.isPending
            ? copy('reviews.sending')
            : existing
              ? copy('reviews.save')
              : copy('reviews.submit')}
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="h-11 border px-6 text-sm font-semibold"
            style={{ borderColor: 'var(--storefront-text)', borderRadius: 'var(--storefront-radius)' }}
          >
            {copy('reviews.cancel')}
          </button>
        ) : null}
      </div>
    </form>
  );
}

/**
 * What the server's refusal means, in the shopper's words.
 *
 * The codes are the review endpoints' own (see the contract): a body that was
 * nothing but markup, one over the limit, a photo that is not one of this
 * shop's uploads, and the per-customer rate limit shared by writing, editing
 * and uploading.
 */
function reviewErrorKey(error: unknown, fallback = 'reviews.error'): string {
  if (!(error instanceof ApiError)) return fallback;
  if (error.status === 429 || error.code === 'RATE_LIMITED') return 'reviews.rate_limited';
  switch (error.code) {
    case 'REVIEW_EMPTY':
      return 'reviews.body_required';
    case 'REVIEW_TOO_LONG':
      return 'reviews.too_long';
    case 'INVALID_PHOTO':
      return 'reviews.photo_error';
    default:
      return fallback;
  }
}

/**
 * Up to four photos, each a thumbnail with a way to take it off again.
 *
 * The "add" control is a real file input inside a label, so it is reachable
 * by keyboard and a screen reader names it; it disappears once four are
 * attached rather than offering a fifth the server would refuse.
 */
function PhotoPicker({
  photos,
  uploading,
  onPick,
  onRemove,
}: {
  photos: string[];
  uploading: boolean;
  onPick: (file: File) => void;
  onRemove: (url: string) => void;
}) {
  const copy = useShopContent();
  const tile = { borderRadius: 'var(--storefront-radius)' };

  return (
    <div className="text-sm">
      <p className="mb-1 font-medium">{copy('reviews.photos_label')}</p>
      <ul className="flex flex-wrap gap-2">
        {photos.map((url, index) => (
          <li key={url} className="relative">
            <StorefrontImage url={url} sizes="80px" placeholder={false} alt="" className="size-20 object-cover" style={tile} />
            <button
              type="button"
              onClick={() => onRemove(url)}
              aria-label={copy('reviews.remove_photo', { number: index + 1 })}
              className="absolute -end-2 -top-2 grid size-6 place-items-center rounded-full"
              style={{ backgroundColor: 'var(--storefront-text)', color: 'var(--storefront-background)' }}
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </li>
        ))}
        {photos.length < MAX_REVIEW_PHOTOS ? (
          <li>
            <label
              className={cn(
                'flex size-20 cursor-pointer flex-col items-center justify-center gap-1 border border-dashed text-center text-xs focus-within:outline focus-within:outline-2',
                uploading && 'cursor-wait opacity-60',
              )}
              style={{ ...tile, borderColor: 'var(--storefront-muted)', color: 'var(--storefront-muted)' }}
            >
              <ImagePlus className="size-5" aria-hidden />
              {uploading ? copy('reviews.uploading') : copy('reviews.add_photo')}
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                disabled={uploading}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) onPick(file);
                  event.target.value = '';
                }}
              />
            </label>
          </li>
        ) : null}
      </ul>
    </div>
  );
}

/**
 * Five stars as a radio group: arrow keys move between them, each is read out
 * as "4 out of 5", and the chosen one and those before it are lit.
 */
function StarPicker({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  const copy = useShopContent();
  const [hover, setHover] = useState(0);
  const lit = hover || value;

  return (
    <fieldset>
      <legend className="mb-1 text-sm font-medium">{copy('reviews.your_rating')}</legend>
      <div className="flex gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((stars) => (
          <label
            key={stars}
            className="cursor-pointer rounded p-0.5 focus-within:outline focus-within:outline-2"
            onMouseEnter={() => setHover(stars)}
          >
            <input
              type="radio"
              name="review-rating"
              value={stars}
              checked={value === stars}
              onChange={() => onChange(stars)}
              className="sr-only"
              aria-label={copy('section.rating', { rating: stars })}
            />
            <Star
              className="size-7"
              style={{ color: stars <= lit ? 'var(--storefront-primary)' : 'var(--storefront-muted)' }}
              fill={stars <= lit ? 'currentColor' : 'none'}
              aria-hidden
            />
          </label>
        ))}
      </div>
    </fieldset>
  );
}
